import api from './api';

const usuarioService = {
  // Obtener todos los usuarios
  getUsuarios: async () => {
    try {
      const response = await api.get('/api/users');
      return response.data;
    } catch (error) {
      console.error('Error al obtener usuarios:', error);
      throw error;
    }
  },

  // Obtener un usuario por ID
  getUsuarioById: async (id) => {
    try {
      const response = await api.get(`/api/users/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error al obtener usuario con ID ${id}:`, error);
      throw error;
    }
  },

  // Crear un nuevo usuario
  createUsuario: async (usuarioData) => {
    try {
      const response = await api.post('/api/users', usuarioData);
      return response.data;
    } catch (error) {
      console.error('Error al crear usuario:', error);
      throw error;
    }
  },

  // Actualizar un usuario existente
  updateUsuario: async (id, usuarioData) => {
    try {
      const response = await api.put(`/api/users/${id}`, usuarioData);
      return response.data;
    } catch (error) {
      console.error(`Error al actualizar usuario con ID ${id}:`, error);
      throw error;
    }
  },

  // Eliminar un usuario
  deleteUsuario: async (id) => {
    try {
      const response = await api.delete(`/api/users/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error al eliminar usuario con ID ${id}:`, error);
      throw error;
    }
  },

  // Obtener empresas con paginación y filtros
  getCompanies: async (page = 1, pageSize = 20, filters = {}) => {
    try {
      // Construir parámetros de consulta
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });

      // Agregar filtros si están presentes (usando los parámetros del backend)
      if (filters.categoria) {
        params.append('category', filters.categoria);
      }
      if (filters.ciudad) {
        params.append('city', filters.ciudad);
      }
      if (filters.estado) {
        params.append('state', filters.estado);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.minYearsExperience) {
        params.append('minYearsExperience', filters.minYearsExperience);
      }

      // Usar el endpoint optimizado con paginación y filtros
      const response = await api.get(`/api/users/companies?${params.toString()}`);

      // El endpoint ya devuelve los datos paginados y filtrados
      return response.data;
    } catch (error) {
      try {
        const fallbackResponse = await api.get(`/api/users/companies?page=${page}&pageSize=${pageSize}`);
        return fallbackResponse.data;
      } catch (fallbackError) {
        throw fallbackError;
      }
    }
  },

  // Obtener todas las categorías y ciudades disponibles para los filtros
  getCompanyFiltersData: async () => {
    try {
      const response = await api.get('/api/users/companies/filters');
      console.log('Datos de filtros obtenidos:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error al obtener datos de filtros:', error);

      // Fallback: obtener todas las empresas para extraer categorías y ciudades únicas
      try {
        console.log('Fallback: extrayendo categorías de todas las empresas...');
        const allCompaniesResponse = await api.get('/api/users/companies?page=1&pageSize=1000');
        const allCompanies = allCompaniesResponse.data?.data || allCompaniesResponse.data?.companies || allCompaniesResponse.data?.users || [];

        const categorias = [...new Set(allCompanies.map(empresa =>
          empresa.category || empresa.businessCategory || empresa.categoria
        ).filter(Boolean))];

        const ciudades = [...new Set(allCompanies.flatMap(empresa => {
          const locations = empresa.locations || empresa.cities || empresa.serviceCities || empresa.ciudades || [];
          return Array.isArray(locations) ? locations.map(c =>
            typeof c === 'string' ? c : c.name || c.nombre || c.city || c.cityName
          ) : [];
        }).filter(Boolean))];

        return { categorias, ciudades };
      } catch (fallbackError) {
        console.error('Error en fallback de filtros:', fallbackError);
        return { categorias: [], ciudades: [] };
      }
    }
  },

  // Obtener partners (usuarios con roles específicos) con paginación y filtros
  getPartners: async (page = 1, pageSize = 20, filters = {}) => {
    try {
      // Construir parámetros de consulta
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });

      // Agregar filtros si están presentes
      if (filters.role) {
        params.append('role', filters.role);
      }
      if (filters.fromDate) {
        params.append('fromDate', filters.fromDate);
      }
      if (filters.toDate) {
        params.append('toDate', filters.toDate);
      }
      if (filters.minBusiness) {
        params.append('minBusiness', filters.minBusiness);
      }
      if (filters.maxBusiness) {
        params.append('maxBusiness', filters.maxBusiness);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }

      // Usar el endpoint optimizado con paginación y filtros
      const response = await api.get(`/api/users/partners?${params.toString()}`);
      console.log('Respuesta del endpoint /api/users/partners con paginación y filtros:', response.data);

      // El endpoint ya devuelve los datos paginados y filtrados
      return response.data;
    } catch (error) {
      console.error('Error al obtener partners con filtros:', error);

      // Fallback: si el backend no soporta filtros, usar solo paginación básica
      try {
        console.log('Intentando fallback con paginación básica...');
        const fallbackResponse = await api.get(`/api/users/partners?page=${page}&pageSize=${pageSize}`);
        console.log('Respuesta del fallback:', fallbackResponse.data);
        return fallbackResponse.data;
      } catch (fallbackError) {
        console.error('Error en fallback:', fallbackError);
        throw fallbackError;
      }
    }
  },

  cancelSubscription: async (companyId) => {
    return new Promise((resolve, reject) => {
      api.delete(`/api/payments/${companyId}`).then((response) => {
        resolve(response);
      }).catch((error) => {
        reject(error.response);
      })
    })
  },

  // Obtener conteo de empresas por categoría
  getCompaniesCountByCategory: async (state = 'active') => {
    try {
      const response = await api.get(`/api/users/companies/count-by-category?state=${state}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener conteo por categoría:', error);
      throw error;
    }
  },

  // Obtener conteo de usuarios por rol
  getUsersCountByRole: async (state = null) => {
    try {
      const url = state
        ? `/api/users/count-by-role?state=${state}`
        : '/api/users/count-by-role';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error al obtener conteo por rol:', error);
      throw error;
    }
  },
};

export default usuarioService;
